import React from 'react';
import { Sparkles, Clock, CheckCircle2, Quote } from 'lucide-react';

/**
 * AISummaryBlock — Phase 4 of LLM Optimization Sprint
 *
 * Purpose: Answer-First / TL;DR block placed near the top of every page so that
 * ChatGPT / Perplexity / Gemini / Claude / Google AI Overviews can extract a
 * high-confidence citation snippet without traversing the full DOM.
 *
 * Key design decisions:
 *  - data-speakable="true"       → picked up by SpeakableSpecification schema
 *  - data-ai-summary="true"      → identifier for AI crawlers + internal audits
 *  - itemProp="abstract"         → schema.org contract for summary text
 *  - Renders synchronously (no Helmet, no lazy children) so pure-HTML crawlers
 *    (GPTBot, CCBot, Amazonbot) can pull it on first paint
 *  - Compact key-facts grid + optional bullet list + optional citation footer
 *
 * Props:
 *  - title         (string)   default "AI Summary"
 *  - answer        (string|node) main paragraph — the extractable citation
 *  - keyFacts      (array<{label, value}>) up to 6 key facts, rendered as chips
 *  - bullets       (array<string>) optional highlights
 *  - updatedLabel  (string)   e.g. "February 2026"
 *  - citation      (string)   optional "Cite as" line
 *  - tone          ("pink"|"blue"|"slate") default "pink"
 *  - compact       (bool)     tighter spacing (for use inside articles)
 */

const TONES = {
  pink: {
    ring: 'border-pink-200',
    bg: 'bg-gradient-to-br from-pink-50 via-white to-rose-50',
    pill: 'bg-pink-100 text-pink-700',
    accent: 'text-pink-600',
    dot: 'bg-pink-500',
  },
  blue: {
    ring: 'border-blue-200',
    bg: 'bg-gradient-to-br from-blue-50 via-white to-indigo-50',
    pill: 'bg-blue-100 text-blue-700',
    accent: 'text-blue-600',
    dot: 'bg-blue-500',
  },
  slate: {
    ring: 'border-slate-200',
    bg: 'bg-gradient-to-br from-slate-50 via-white to-slate-50',
    pill: 'bg-slate-200 text-slate-700',
    accent: 'text-slate-700',
    dot: 'bg-slate-500',
  },
};

const AISummaryBlock = ({
  title = 'AI Summary',
  answer,
  keyFacts = [],
  bullets = [],
  updatedLabel = 'February 2026',
  citation,
  tone = 'pink',
  compact = false,
}) => {
  if (!answer && !keyFacts.length && !bullets.length) return null;
  const t = TONES[tone] || TONES.pink;

  return (
    <section
      className={`${compact ? 'my-6' : 'my-8 md:my-10'}`}
      data-ai-summary="true"
      data-testid="ai-summary-block"
      aria-labelledby="ai-summary-heading"
      itemScope
      itemType="https://schema.org/CreativeWork"
    >
      <div className={`max-w-4xl mx-auto rounded-2xl border-2 ${t.ring} ${t.bg} ${compact ? 'p-5 md:p-6' : 'p-6 md:p-8'} shadow-sm`}>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className={`inline-flex items-center gap-2 ${t.pill} px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider`}>
            <Sparkles className="w-3.5 h-3.5" />
            <span id="ai-summary-heading" data-testid="ai-summary-title">{title}</span>
          </div>
          <div className="inline-flex items-center gap-1.5 text-xs text-slate-500">
            <Clock className="w-3.5 h-3.5" />
            <time itemProp="dateModified" dateTime="2026-02">{updatedLabel}</time>
            <span className="mx-1">·</span>
            <span>Fact-checked</span>
          </div>
        </div>

        {/* Main Answer — the extractable citation */}
        {answer && (
          <p
            className={`text-slate-800 leading-relaxed ${compact ? 'text-base' : 'text-base md:text-lg'} mb-4`}
            data-speakable="true"
            data-testid="ai-summary-answer"
            itemProp="abstract"
          >
            {answer}
          </p>
        )}

        {/* Bullet highlights (optional) */}
        {bullets.length > 0 && (
          <ul className="space-y-2 mb-4" data-testid="ai-summary-bullets">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-700 text-sm md:text-base leading-relaxed" data-speakable="true">
                <CheckCircle2 className={`w-4 h-4 mt-1 flex-shrink-0 ${t.accent}`} aria-hidden="true" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Key facts grid (optional) */}
        {keyFacts.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mt-4" data-testid="ai-summary-keyfacts">
            {keyFacts.slice(0, 6).map((f, i) => (
              <div
                key={i}
                className="bg-white/70 backdrop-blur-sm border border-white rounded-xl px-3 py-2.5"
                data-testid={`ai-summary-fact-${i}`}
              >
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{f.label}</div>
                <div className={`text-sm md:text-base font-semibold text-slate-900 mt-0.5`}>{f.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Citation footer (optional) */}
        {citation && (
          <div className="mt-5 pt-4 border-t border-slate-200/70 flex items-start gap-2 text-xs text-slate-500">
            <Quote className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span data-testid="ai-summary-citation">
              <strong className="text-slate-600">Cite as:</strong> {citation}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default AISummaryBlock;
export { AISummaryBlock };
